import { viewActionCreators } from '@opengov/component-library-classic/module/view';
import { OpenGovAppConfig } from '@opengov/app-config';
import {
  Button,
  Form,
  Icon,
  Label,
  LoadingSpinner,
  RadioButton,
  RadioGroup,
  Switch
} from '@opengov/component-library/capital';
import { createPortal } from 'react-dom';
import { RemoveEmbedModal } from '../RemoveEmbedModal/RemoveEmbedModal';
import { embed as embedSDK, IParamsV3 } from '@opengov/reporting-embeds';
import { EmbedTypeV3, ICurrentOrLegacyReport, IEmbedDataV3, IEmbedV3, IMergedReport } from '@opengov/reporting-types';
import { IStoryContent } from '@opengov/stories-shared/types';

import cx from 'classnames2';
import { debounce, isEqual } from 'lodash';
import React from 'react';
import Select from 'react-select';
import { v1 as uuid } from 'uuid';

import {
  EMBED_IFRAME_DEFAULT_HEIGHT,
  EMBED_IFRAME_DEFAULT_WIDTH,
  MEDIA_TILE,
  TAG_DIV,
  TAG_SCRIPT
} from '../../../stories/constants';
import { addMedia, getVisualizationSelectedValue, removeMedia } from '../../../stories/utils';
import { CHART_TYPES, vizOptionsChartsDetails, vizOptionsMds } from '../../../stories/utils/visualizationSelect';
import { IEmbed, IHandleEmbedPayload } from '../../../types';
import { Modal } from '../Modal';
import modalStyles from '../Modal/Modal.scss';

import styles from './AddTile.scss';
import { ReportOption } from './ReportOption';
import { TableColumnDropdowns } from './TableColumnDropdownsComponent/TableColumnDropdowns';
import { VisualizationOption, VisualizationValue } from './VisualizationSelectComponent';
import { bumpPublicationUpdatedAt } from '../../../stories/components/Publications/PublicationView/hooks/usePublicationReportEmbedMutations';
import { IEmbedCreateEmbedPayload } from './types';
import {
  applyTemplateReplacement,
  extractMessage,
  getMediaIndexByEmbedId,
  mediaDivTag,
  mediaScriptTag
} from '../../../stories/common/utils';
import { IAddTileProps } from './AddTile';
import { resolveStoryEmbedCreatedAt } from './resolveStoryEmbedCreatedAt';

interface IAddTileState {
  isUpdating: boolean;
  shouldDisableButton: boolean;
  isSnippetVisible: boolean;
  embedId: string;
  embedVersion: string;
  updateSetting: string; // 'snapshot' | 'refresh-on-update' | 'live';
  disableClickThrough: boolean;
  title: string;
  description: string;
  report: Partial<IMergedReport>;
  savedView: {
    id: string;
    name: string;
  };
  chartType: {
    value: string;
    name: string;
  };
  hasFailedReportEmbed: boolean;
  updateActionController: React.MutableRefObject<AbortController>;
  previewActionController: React.MutableRefObject<AbortController>;
  generatePreviewController: React.MutableRefObject<AbortController>;
  isDeleteEmbedinProgress: boolean;
  v2EmbedFormDisabled: boolean;
  showDeleteModal?: boolean;
}

interface IHandleUpdateEmbedArg {
  title?: string;
  description?: string;
  shouldSync?: boolean;
  type?: string;
  selectedGroupings?: string[];
  columnId?: string;
  targetColumnId?: string;
  valueRowId?: string;
}

const operateUrl = OpenGovAppConfig.OPERATE_URL;
const embedSdkApiv2Prefix = '/api/embed_service/v2/embed';
const embedSdkApiv3Prefix = '/api/embed_service/v3/embeds';
const embedSdkApiv4Prefix = '/api/embed_service/v4/embeds';

export class AddTileOld extends React.Component<IAddTileProps, IAddTileState> {
  publication: any;

  constructor(props: IAddTileProps) {
    super(props);
    // Deconstruct prop object
    const {
      storyContent: { content, mediaIndex = 0 },
      isReportEmbedListSelected
    } = props;
    // Default state
    let isUpdating = false;
    // Create a document fragment based on the module content html string
    const fragment = document.createRange().createContextualFragment(content);
    // Find the media block inside the document fragment
    const mediaBlocks = fragment.querySelectorAll('.media-block');
    const mediaBlock = mediaBlocks[mediaIndex];
    // Pull Embed ID from script attributes
    let embedId = null;
    let embedVersion = '';
    // Pull Embed update setting from div attributes
    let updateSetting = 'snapshot'; // Default

    let disableClickThrough = false;
    if (!isReportEmbedListSelected) {
      const embedValues = this.fetchStoryEmbedDetails(mediaBlock);
      isUpdating = embedValues.isUpdating;
      updateSetting = embedValues.updateSetting;
      disableClickThrough = embedValues.disableClickThrough;
      embedId = embedValues.embedId;
      embedVersion = embedValues.embedVersion;
    }
    this.handleUpdateEmbed = debounce(this.handleUpdateEmbed, 500);
    this.state = {
      isUpdating,
      shouldDisableButton: false,
      isSnippetVisible: false,
      title: '',
      description: '',
      report: {
        id: null,
        name: ''
      },
      savedView: {
        id: null,
        name: ''
      },
      chartType: {
        value: null,
        name: ''
      },
      hasFailedReportEmbed: false,
      isDeleteEmbedinProgress: false,
      embedId,
      embedVersion,
      updateSetting,
      disableClickThrough,
      updateActionController: React.createRef(),
      previewActionController: React.createRef(),
      generatePreviewController: React.createRef(),
      showDeleteModal: false,
      v2EmbedFormDisabled: false
    };
  }

  componentDidMount() {
    const { embedId, embedVersion } = this.state;
    const {
      canUseV3Embeds,
      embedV2EditingEnabled,
      entity,
      entityId,
      selectedEmbed,
      cloneEmbedById,
      getReports,
      getSavedViews,
      getSavedViewInfo,
      resetEmbed,
      resetReports,
      resetSavedViews,
      unsetIsCreatingEmbed
    } = this.props;
    const embedIdToUpdate = embedId || selectedEmbed?.embedId;
    // Clear embed in store
    resetEmbed();
    // List of reports for current entity
    void getReports({
      entityId,
      includeMultiDatasetReports: canUseV3Embeds
    }).then(async (reports: ICurrentOrLegacyReport[]) => {
      try {
        let title: string;
        let description: string;
        let reportId: string;
        let savedViewId: string;
        let reportVersion: string;
        let version: string;
        let embedDetails: IEmbed;
        if (!!selectedEmbed) embedDetails = await this.props.getEmbed({ id: selectedEmbed.embedId });
        // Get embed data if id is available and create new embed for preview
        if (embedIdToUpdate) {
          cloneEmbedById({
            id: embedIdToUpdate,
            embedVersion: !!selectedEmbed ? embedDetails.version : embedVersion
          })
            .then((embed: (IEmbedV3 | IEmbed) & { reportVersion: string }) => {
              if (embedVersion === 'v2' || embed.data?.tile?.version === 'v2' || embed?.data?.version === 'v2') {
                ({
                  title,
                  description,
                  reportId,
                  savedViewId,
                  report_version: reportVersion,
                  version
                } = embed?.data?.tile || embed?.data);
              } else {
                ({ title, description = '', reportId, savedViewId, reportVersion = '', version } = embed);
              }
              this.setState({
                title,
                description,
                report: {
                  id: reportId,
                  name: ''
                },
                savedView: {
                  id: savedViewId,
                  name: ''
                },
                embedVersion: version,
                isUpdating: true,
                v2EmbedFormDisabled:
                  !embedV2EditingEnabled && (embedVersion === 'v2' || version === 'v2') && reportVersion === 'v2'
              });
              reportVersion = reportVersion || 'v2';

              if (selectedEmbed) {
                void this.fetchStoryContent().then((storyContents) => {
                  let mediaIndexUpdate = 0;
                  const mediaIndexStoryContent = getMediaIndexByEmbedId(selectedEmbed.embedId, storyContents);
                  if (!mediaIndexStoryContent) {
                    return;
                  }
                  const updateEmbedContent = mediaIndexStoryContent.content;
                  mediaIndexUpdate = mediaIndexStoryContent.mediaindex;
                  if (!updateEmbedContent) {
                    return;
                  }
                  const fragment = document.createRange().createContextualFragment(updateEmbedContent.content);
                  const mediaBlocks = fragment.querySelectorAll('.media-block');
                  mediaBlocks.forEach((mediaBlock) => {
                    for (let i = 0; i < mediaBlock.children.length; i++) {
                      if (
                        mediaBlock.children[i].tagName === TAG_DIV &&
                        mediaBlock.children[i].hasAttribute('data-embed-id')
                      ) {
                        const embedId = mediaBlock.children[i].getAttribute('data-embed-id');
                        if (selectedEmbed.embedId === embedId) {
                          mediaIndexUpdate = +mediaBlock.getAttribute('data-media-index') - 1;
                          break;
                        }
                      }
                    }
                  });
                  const selectedEmbedMediaBlock = mediaBlocks[mediaIndexUpdate];
                  const { updateSetting, disableClickThrough } = this.fetchStoryEmbedDetails(selectedEmbedMediaBlock);
                  this.setState({
                    updateSetting,
                    disableClickThrough,
                    embedId: embedIdToUpdate
                  });
                });
              }
              if (reportVersion !== 'v2') {
                const selectedReport = reports.find((r) => r.id === reportId);
                void getSavedViews({
                  legacyReportId: selectedReport.legacyReportId,
                  reportVersion,
                  entity
                }).then((savedViews) => {
                  const selectedSavedView = savedViews.find((sv) => sv.id === savedViewId);
                  getSavedViewInfo({ savedViewId: selectedSavedView.legacySavedViewId });
                });
              } else {
                void getSavedViews({ reportId, reportVersion, entity });
              }
              void this.generatePreview(version, embed);
              this.props.onEmbedChange(embed);
            })
            .catch((err) => {
              resetSavedViews();
              resetReports();
              unsetIsCreatingEmbed();
              this.setState((prevState) => ({ ...prevState, hasFailedReportEmbed: true, isUpdating: true }));
              this.props.onEmbedError();
            });
        }
      } catch (e) {
        this.props.onEmbedError();
      }
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { embed } = this.props;
    const { disableClickThrough } = this.state;
    const version = embed?.data?.tile?.version || embed?.data?.version || embed.version;
    if (
      (!isEqual(prevProps.embed, embed) || disableClickThrough !== prevState.disableClickThrough) &&
      embed.data !== null
    ) {
      // Generate new preview if embed has changed
      void this.generatePreview(version, embed);
    }
  }

  fetchStoryContent = async (): Promise<IStoryContent[]> => {
    const { selectedEmbed, entityId, entitySubdomain } = this.props;
    const storyContents = await this.props.getStoryContent({
      storyId: selectedEmbed.story._id,
      entityId,
      entitySubdomain,
      isTemplate: false
    });
    return storyContents;
  };

  fetchStoryEmbedDetails = (
    mediaBlock
  ): {
    updateSetting: string;
    isUpdating: boolean;
    disableClickThrough: boolean;
    embedId: string;
    embedVersion: string;
  } => {
    let updateSetting = 'snapshot';
    let isUpdating = false;
    let disableClickThrough;
    let embedId = null;
    let embedVersion = '';

    for (let i = 0; i < mediaBlock.children.length; i++) {
      if (mediaBlock.children[i].tagName === TAG_DIV && mediaBlock.children[i].hasAttribute('data-update-setting')) {
        updateSetting = mediaBlock.children[i].getAttribute('data-update-setting');
        isUpdating = true;
      }

      if (
        mediaBlock.children[i].tagName === TAG_DIV &&
        mediaBlock.children[i].hasAttribute('data-embed-disable-click')
      ) {
        disableClickThrough = mediaBlock.children[i].getAttribute('data-embed-disable-click') === `true`;
        isUpdating = true;
      }

      if (mediaBlock.children[i].tagName === TAG_SCRIPT && mediaBlock.children[i].hasAttribute('data-embed-id')) {
        embedId = mediaBlock.children[i].getAttribute('data-embed-id');
        embedVersion = mediaBlock.children[i].getAttribute('data-embed-version');
        isUpdating = true;
      }
    }
    return { updateSetting, isUpdating, disableClickThrough, embedId, embedVersion };
  };

  onReportChange = (value) => {
    const {
      canUseV3Embeds,
      entity,
      resetEmbed,
      resetSavedViewInfo,
      resetSavedViews,
      resetSelectedGroupings,
      resetGroupDataBy,
      getSavedViews,
      reports,
      accountSummaryChartsInEmbedEnabled
    } = this.props;
    // Clear embed in store
    resetEmbed();
    // Clear the selected groupings in store
    resetSelectedGroupings();
    // Clear the selected group by in store
    resetGroupDataBy();
    // Clear saved view info in store
    resetSavedViewInfo();
    // Clear saved views in store
    resetSavedViews();
    this.setState({
      savedView: {
        id: null,
        name: ''
      },
      title: '',
      description: '',
      embedVersion: 'v2'
    });
    // If report value is cleared
    if (value && !value.id && !value.name) {
      // Clear report in state
      this.setState({
        report: {
          id: null,
          name: ''
        }
      });
    } else {
      // Set report in state
      this.setState({ report: value, hasFailedReportEmbed: false });
      const selectedReport = reports.find((r) => r.id === value.id);
      if (selectedReport.version !== 'v2') {
        getSavedViews({ legacyReportId: selectedReport.legacyReportId, reportVersion: selectedReport.version, entity });
      } else {
        if (accountSummaryChartsInEmbedEnabled && selectedReport?.isMultiDataset) {
          this.setState({ embedVersion: 'v4' });
        } else if (canUseV3Embeds) {
          this.setState({ embedVersion: 'v3' });
        }

        getSavedViews({ reportId: selectedReport.id, reportVersion: selectedReport.version, entity });
      }
    }
  };

  isSavedViewCleared = (value) => {
    return !value.id && !value.name;
  };

  isReportAndViewSelected = (value) => {
    const { report } = this.state;
    return report.id && value.id;
  };

  onClearSavedView = () => {
    const { resetEmbed, resetSavedViewInfo, resetSelectedGroupings, resetGroupDataBy } = this.props;
    this.setState({
      savedView: {
        id: null,
        name: ''
      },
      title: '',
      description: ''
    } as Pick<IAddTileState, keyof IAddTileState>);
    resetEmbed();
    resetSavedViewInfo();
    resetSelectedGroupings();
    resetGroupDataBy();
  };

  onSavedViewChange = async (value) => {
    const { report, embedId: id, previewActionController } = this.state;
    const {
      embed: { version },
      reports,
      savedViews,
      groupDataBy,
      selectedGroupings,
      messages,
      getSavedViewInfo,
      previewEmbed
    } = this.props;

    if (previewActionController.current) {
      previewActionController.current.abort(messages.previewEmbedErrorMessage);
    }
    const controller = new AbortController();
    previewActionController.current = controller;

    const selectedSavedView = savedViews.find((sv) => sv.id === value.id);
    const name: string = (selectedSavedView && selectedSavedView.name) || '';
    const title: string = name.length > 60 ? `${name.substring(0, 57)}...` : name;
    const description = selectedSavedView && selectedSavedView.description;

    let embedVersion = 'v2';
    if (version) {
      embedVersion = version === 'v2' || version === 'v3' || version === 'v4' ? version : 'v1';
    } else if (this.state.embedVersion) {
      embedVersion = this.state.embedVersion;
    }

    this.setState({
      savedView: value,
      title,
      description,
      embedVersion,
      embedId: id
    } as Pick<IAddTileState, keyof IAddTileState>);

    if (this.isSavedViewCleared(value)) {
      this.onClearSavedView();
    } else if (this.isReportAndViewSelected(value)) {
      const selectedReport = reports.find((r) => r.id === report.id);
      let previewedEmbed;
      try {
        if (embedVersion === 'v3' || embedVersion === 'v4') {
          previewedEmbed = await previewEmbed({
            id,
            title,
            description,
            reportId: report.id,
            savedViewId: value.id,
            shouldSync: false,
            reportVersion: selectedReport.version,
            version: embedVersion,
            controller: previewActionController.current
          });
        } else {
          const reportVersion = selectedReport.version === 'v2' ? 'v2' : 'v1';
          if (reportVersion === 'v1') {
            getSavedViewInfo({ savedViewId: selectedSavedView.legacySavedViewId.toString() });
          }
          previewedEmbed = await previewEmbed({
            id,
            title,
            description,
            report_id: report.id,
            saved_view_id: value.id,
            should_sync: false,
            report_version: reportVersion,
            version: embedVersion,
            group_data_by: groupDataBy,
            table_columns: selectedGroupings,
            controller: previewActionController.current
          });
        }
        if (!previewActionController.current.signal.aborted) {
          this.props.onEmbedChange(previewedEmbed);
        }
      } catch (error) {
        this.showTileError(error, messages.errorPreviewEmbed, messages.somethingWentWrong);
      }
    }
  };

  onClickThroughSettingChange = () => {
    const { disableClickThrough } = this.state;
    this.setState({ disableClickThrough: !disableClickThrough });
  };

  onUpdateSettingChange = (value) => {
    const { updateSetting } = this.state;

    if (value.id !== updateSetting) {
      this.setState({ updateSetting: value.id });
    }
  };

  onTableVisualizationSettingsGroupChange = (selectedGroupBy: string) => {
    const { groupDataBy, setGroupDataBy, resetSelectedGroupings, selectedGroupings } = this.props;

    // Generate new Table Tile if selectedGroupBy is updated and selectedGroupings has data
    if (selectedGroupBy !== groupDataBy && selectedGroupings && selectedGroupings.length > 0) {
      resetSelectedGroupings();
      void this.handleUpdateEmbed({ type: 'transparency_table', selectedGroupings: [] });
    }
    setGroupDataBy(selectedGroupBy);
  };

  showEmbedSnippet = () => {
    this.setState({ isSnippetVisible: true });
  };

  generatePreview = async (version: string, embed: IEmbed | IEmbedV3) => {
    const { disableClickThrough, generatePreviewController } = this.state;
    const { messages } = this.props;
    if (generatePreviewController.current) {
      generatePreviewController.current.abort(messages.previewEmbedErrorMessage);
    }
    if (version === 'v3' || version === 'v4') {
      await embedSDK({
        version,
        baseUrl: operateUrl,
        embed: { ...embed, version, preview: true },
        targetSelector: '#tile-preview',
        disableClick: disableClickThrough,
        onStart: () => {},
        controller: generatePreviewController.current
      } as IParamsV3);
    } else {
      const embedVersion = version === 'v2' ? 'v2' : 'v1';
      const { html, css, ...embedData } = embed.data;
      await embedSDK({
        version: embedVersion,
        baseUrl: operateUrl,
        embed: {
          preview: true,
          id: embed?.data?.id || embed?.data?.tile?.id,
          data: { data: embedData, html, css },
          type: embed?.data?.type,
          version: embedVersion
        },
        targetSelector: '#tile-preview',
        disableClick: disableClickThrough,
        onStart: () => {},
        controller: generatePreviewController.current
      });
    }
  };

  hasReportAndSavedView = () => {
    const { report, savedView } = this.state;
    return !!(report.id && savedView.id);
  };

  isSavedViewVisualizationsEmpty = () => {
    const { savedViewInfo } = this.props;
    return Array.isArray(savedViewInfo.visualizations) && savedViewInfo.visualizations.length === 0;
  };

  isControlDisabled = () => {
    return this.isSavedViewVisualizationsEmpty();
  };

  showTileError = (e: Error | string, errorTitle: string, errorMessage: string) => {
    const { messages } = this.props;
    const isAbort =
      e === messages.previewEmbedErrorMessage ||
      (e != null && typeof e === 'object' && 'name' in e && (e as Error).name === 'AbortError') ||
      (e != null && typeof e === 'object' && 'code' in e && (e as { code?: number }).code === DOMException.ABORT_ERR);
    if (isAbort) {
      return true;
    }
    const {
      unsetIsCreatingEmbed,
      resetEmbed,
      resetSelectedGroupings,
      resetGroupDataBy,
      resetSavedViewInfo,
      resetSavedViews,
      resetReports
    } = this.props;
    viewActionCreators.showCapitalToast({
      title: errorTitle,
      body: extractMessage(e as Error, errorMessage),
      variant: 'failure'
    });
    unsetIsCreatingEmbed();
    resetEmbed();
    resetSelectedGroupings();
    resetGroupDataBy();
    resetSavedViewInfo();
    resetSavedViews();
    resetReports();
  };

  handleClose = () => {
    const {
      messages,
      unsetIsCreatingEmbed,
      onClose,
      resetEmbed,
      resetReports,
      resetSavedViewInfo,
      resetSavedViews,
      resetSelectedGroupings,
      resetGroupDataBy
    } = this.props;

    const { updateActionController, previewActionController } = this.state;

    // Reset the store data
    unsetIsCreatingEmbed();
    resetEmbed();
    resetSelectedGroupings();
    resetGroupDataBy();
    resetSavedViewInfo();
    resetSavedViews();
    resetReports();

    if (updateActionController.current) {
      updateActionController.current.abort(messages.previewEmbedErrorMessage);
    }

    if (previewActionController.current) {
      previewActionController.current.abort(messages.previewEmbedErrorMessage);
    }

    // Close the modal
    onClose();
  };

  handleRemove = async () => {
    const { updateActionController, previewActionController } = this.state;
    const {
      isTemplate,
      story,
      storyContent,
      isReportEmbedListSelected,
      storyContent: { mediaIndex = 0 },
      selectedEmbed,
      displayReportEmbeds: _displayReportEmbeds,
      user,
      publication,
      messages,
      updateStoryContent,
      updateStoryContentDB,
      deleteStoryEmbed,
      deleteReportEmbeds
    } = this.props;
    const updateEmbedContent = storyContent;
    this.setState({ isDeleteEmbedinProgress: true });
    const embedId = isReportEmbedListSelected ? selectedEmbed?.embedId : this.state.embedId;
    if (updateActionController.current) {
      updateActionController.current.abort(messages.previewEmbedErrorMessage);
    }
    if (previewActionController.current) {
      previewActionController.current.abort(messages.previewEmbedErrorMessage);
    }
    if (!isReportEmbedListSelected) {
      updateStoryContent(removeMedia(updateEmbedContent, mediaIndex));
      await updateStoryContentDB({
        storyContent: removeMedia(updateEmbedContent, mediaIndex),
        story: story,
        isTemplate: isTemplate
      });
      await deleteStoryEmbed({
        storyId: story._id,
        embedId: embedId
      });
    }
    if (isReportEmbedListSelected) {
      await deleteReportEmbeds({
        deletePayload: [{ storyId: story._id, embedId }],
        publicationId: publication.id,
        userId: user.id,
        type: selectedEmbed.type,
        isReportEmbedListSelected
      });
    }
    this.setState({ isDeleteEmbedinProgress: false });
    this.handleClose();
  };

  handleClick = async (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const {
      embed,
      groupDataBy,
      isTemplate,
      isCreatingEmbed,
      entityId,
      messages,
      selectedGroupings,
      story,
      storyContent,
      selectedEmbed,
      storyContent: { mediaIndex = 0 },
      entitySubdomain,
      publication,
      displayReportEmbeds,
      isReportEmbedListSelected,
      unsetIsCreatingEmbed,
      deleteStoryEmbed,
      createEmbed,
      createV3Embed,
      createV4Embed,
      updateStoryContent,
      handleStoryEmbeds,
      updateStoryContentDB,
      user
    } = this.props;

    const {
      disableClickThrough,
      updateSetting,
      report,
      savedView,
      embedId: id,
      isUpdating,
      updateActionController
    } = this.state;
    const uniqueId = uuid();
    let embedUpdateContent = storyContent;
    let mediaIndexUpdate = mediaIndex ?? 0;
    let createdAt = new Date().toISOString();
    let createEmbedResponse;
    let embedType: string;
    if (updateActionController.current) {
      updateActionController.current.abort(messages.previewEmbedErrorMessage);
    }
    try {
      const controller = new AbortController();
      updateActionController.current = controller;

      if (selectedEmbed) {
        const storyContents = await this.props.getStoryContent({
          storyId: selectedEmbed.story._id,
          entityId,
          entitySubdomain,
          isTemplate: false
        });
        const mediaIndexStoryContent = getMediaIndexByEmbedId(selectedEmbed.embedId, storyContents);
        if (mediaIndexStoryContent) {
          embedUpdateContent = mediaIndexStoryContent.content;
          mediaIndexUpdate = mediaIndexStoryContent.mediaindex;
        }
      }
      if (this.state.embedVersion === 'v4') {
        embedType = embed.type;
        createEmbedResponse = await createV4Embed(
          {
            title: this.state.title,
            reportId: report.id,
            savedViewId: savedView.id,
            description: this.state.description,
            shouldSync: updateSetting === 'live',
            type: embedType,
            reportVersion: 'v2',
            data: embed?.data
          },
          isReportEmbedListSelected
        );
      } else if (this.state.embedVersion === 'v3') {
        embedType = embed.type;
        createEmbedResponse = await createV3Embed(
          {
            title: this.state.title,
            reportId: report.id,
            savedViewId: savedView.id,
            description: this.state.description,
            shouldSync: updateSetting === 'live',
            type: embedType,
            reportVersion: 'v2',
            data: embed?.data
          },
          isReportEmbedListSelected
        );
      } else {
        embedType = (CHART_TYPES[embed.data?.type] || embed.data?.type) as string;
        let createEmbedPayload: IEmbedCreateEmbedPayload = {
          title: this.state.title,
          reportId: report.id,
          savedViewId: savedView.id,
          description: this.state.description,
          shouldSync: updateSetting === 'live',
          version: this.state.embedVersion,
          type: embedType,
          reportVersion: embed.data?.report_version || embed.data?.tile?.report_version,
          groupDataBy,
          tableColumns: selectedGroupings,
          addOrUpdatePayload: isReportEmbedListSelected
        };
        if (!isUpdating) createEmbedPayload = { ...createEmbedPayload, id };
        createEmbedResponse = await createEmbed(createEmbedPayload);
      }

      if (updateActionController.current.signal.aborted || isCreatingEmbed) {
        unsetIsCreatingEmbed();
        return;
      }
      // An id-less response would be written into the story content as data-embed-id="undefined"
      // and permanently break the tile, so fail before anything is persisted.
      if (!createEmbedResponse?.id) {
        throw new Error('Created embed is missing an id');
      }
      const embedId = createEmbedResponse.id;

      this.setState({ shouldDisableButton: true });
      const mediaDivReplacements = {
        disableClick: !!disableClickThrough,
        uniqueId,
        updateSetting,
        embedId,
        privacyTag: report.isPrivate ? `data-warning="${messages.tileWarning}"` : '',
        version: createEmbedResponse.version
      };
      const mediaDiv = applyTemplateReplacement(mediaDivTag, mediaDivReplacements);
      const mediaScriptReplacements = {
        disableClick: !!disableClickThrough,
        uniqueId,
        operateUrl,
        embedId,
        version: createEmbedResponse.version
      };

      const mediaScript = applyTemplateReplacement(mediaScriptTag, mediaScriptReplacements);
      const metaStoryContent = removeMedia(embedUpdateContent, mediaIndexUpdate);
      const newStoryContent = addMedia(metaStoryContent, mediaDiv, mediaIndexUpdate, MEDIA_TILE);
      const finalStoryContent = addMedia(newStoryContent, mediaScript, mediaIndexUpdate, MEDIA_TILE);
      let handleStoryEmbedPayload = {
        story: { ...story, embeds: (story.embeds ?? []).concat(createEmbedResponse) },
        isTemplate,
        entityId
      } as IHandleEmbedPayload;

      if (isUpdating && displayReportEmbeds) {
        const embedIdForUpdate = !!selectedEmbed ? selectedEmbed.embedId : this.state.embedId;
        const deleteResponse = await deleteStoryEmbed({
          storyId: story._id,
          embedId: embedIdForUpdate,
          controller: updateActionController.current
        });
        // Missing storyEmbeds rows return null; keep the fallback createdAt instead of throwing.
        createdAt = resolveStoryEmbedCreatedAt(deleteResponse, createdAt);
      }
      if (displayReportEmbeds) {
        handleStoryEmbedPayload = {
          ...handleStoryEmbedPayload,
          storyEmbedUpdatePayload: {
            [embedId]: {
              id: embedId,
              storyId: story?._id || selectedEmbed?.story._id,
              frequency: updateSetting,
              type: embedType,
              reportId: report.id,
              savedViewId: savedView.id,
              authorId: story.author._id,
              isUpdating: this.state.isUpdating,
              createdAt
            }
          },
          isUpdatingReportEmbed: this.props.isReportEmbedListSelected,
          publicationId: publication?.id,
          displayReportEmbeds
        };
      }

      updateStoryContent(finalStoryContent);
      await updateStoryContentDB({
        story: story,
        isTemplate: isTemplate,
        storyContent: finalStoryContent,
        controller: updateActionController.current
      });
      await handleStoryEmbeds(handleStoryEmbedPayload);

      if (!updateActionController.current.signal.aborted) {
        if (publication?.id) void bumpPublicationUpdatedAt(publication.id, user.id);
        this.setState({ shouldDisableButton: false });
        // Reset state and close modal
        this.handleClose();
      }
    } catch (error) {
      // A save superseded by a newer one is not a failure to report.
      if (updateActionController.current?.signal.aborted) {
        unsetIsCreatingEmbed();
        return;
      }
      this.showTileError(error, messages.errorEmbedAddUpdate, messages.somethingWentWrong);
    }
  };

  handleUpdateEmbed = async ({
    title = this.state.title,
    description = this.state.description,
    shouldSync = false,
    type,
    selectedGroupings = this.props.selectedGroupings,
    columnId,
    targetColumnId,
    valueRowId
  }: IHandleUpdateEmbedArg) => {
    const { reports, previewEmbed, groupDataBy, savedViews, embed, user, publication, messages, resetEmbed } =
      this.props;
    resetEmbed();
    const chartType: string = type ?? (embed.type || embed.data?.type);
    const { report, savedView, embedId: id, embedVersion, previewActionController } = this.state;
    const selectedReport = reports.find((r) => r.id === report.id);
    const selectedSavedView = savedViews.find((sv) => sv.id === savedView.id);
    const embedDataOptions = (embed.data as IEmbedDataV3)?.options;
    const selectedColumnId = columnId || embedDataOptions?.columnId;
    const selectedTargetColumnId = targetColumnId || embedDataOptions?.targetColumnId;
    const selectedValueRowId = valueRowId || embedDataOptions?.rowGroupId;
    const dataOptions =
      selectedColumnId || selectedTargetColumnId || selectedValueRowId
        ? {
            options: {
              columnId: selectedColumnId,
              targetColumnId: selectedTargetColumnId,
              rowGroupId: selectedValueRowId
            }
          }
        : null;
    let previewedEmbed;

    if (previewActionController.current) {
      previewActionController.current.abort(messages.previewEmbedErrorMessage);
    }
    const controller = new AbortController();
    previewActionController.current = controller;

    try {
      if (embedVersion === 'v3' || embedVersion === 'v4') {
        previewedEmbed = await previewEmbed({
          id,
          title,
          description,
          reportId: selectedReport.id,
          savedViewId: selectedSavedView.id,
          shouldSync,
          reportVersion: selectedReport.version,
          type: chartType,
          version: embedVersion,
          data: dataOptions,
          controller: previewActionController.current
        });
      } else {
        const type: string = CHART_TYPES[chartType] || chartType;
        previewedEmbed = await previewEmbed({
          id,
          title,
          description,
          report_id: selectedReport.id,
          saved_view_id: selectedSavedView.id,
          type,
          should_sync: shouldSync,
          report_version: selectedReport.version,
          group_data_by: groupDataBy,
          table_columns: selectedGroupings,
          version: embedVersion,
          controller: previewActionController.current
        });
      }
      this.props.onEmbedChange(previewedEmbed);
      if (publication?.id) void bumpPublicationUpdatedAt(publication.id, user.id);
    } catch (error) {
      this.showTileError(error, messages.errorEmbedAddUpdate, messages.somethingWentWrong);
    }
  };

  handleTitle = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const title = event.target.value;
    this.setState({ title });
    void this.handleUpdateEmbed({ title });
  };

  handleDescription = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    event.preventDefault();
    const description = event.target.value;
    this.setState({ description });
    void this.handleUpdateEmbed({ description });
  };

  shouldHideTitleAndDescription = (): boolean => {
    const {
      embedVersion,
      report: { id: reportId }
    } = this.state;
    const { accountSummaryChartTitleAndDescriptionEnabled, reports } = this.props;

    const selectedReport = reports.find((r) => r.id === reportId);

    return !!(embedVersion === 'v4' && accountSummaryChartTitleAndDescriptionEnabled && selectedReport?.isMultiDataset);
  };

  renderTitleInput = (): JSX.Element => {
    const { title, v2EmbedFormDisabled } = this.state;
    const { messages } = this.props;
    const titleLength = title?.length ?? 0;

    if (this.shouldHideTitleAndDescription()) {
      return <></>;
    }

    return (
      <fieldset className="ui-form">
        <Form.Item label={messages.title}>
          <Form.CharacterCounter currentCount={titleLength} maxCount={60} />
          <input
            type="text"
            name={messages.title}
            value={title}
            maxLength={60}
            onChange={this.handleTitle}
            data-test="graph-title"
            disabled={!this.hasReportAndSavedView() || v2EmbedFormDisabled}
          />
        </Form.Item>
      </fieldset>
    );
  };

  renderDescriptionInput = (): JSX.Element => {
    const { description, v2EmbedFormDisabled } = this.state;
    const { messages } = this.props;
    const descriptionLength = description?.length ?? 0;

    if (this.shouldHideTitleAndDescription()) {
      return <></>;
    }

    return (
      <fieldset className={cx(styles.description, 'ui-form')}>
        <Form.Item label={messages.description}>
          <Form.CharacterCounter currentCount={descriptionLength} maxCount={140} />
          <textarea
            value={description}
            maxLength={140}
            onChange={this.handleDescription}
            data-test="graph-description"
            disabled={!this.hasReportAndSavedView() || v2EmbedFormDisabled}
          />
        </Form.Item>
      </fieldset>
    );
  };

  renderDataDisplaySelect = (): JSX.Element => {
    const { embed, messages, visualizationSelectOptions } = this.props;

    return (
      <fieldset className="ui-form">
        <Label>{messages.dataDisplay}</Label>
        <Select
          clearable={false}
          name="visualization"
          labelKey="name"
          valueKey="name"
          optionComponent={VisualizationOption}
          options={visualizationSelectOptions}
          placeholder={messages.selectDataDisplay}
          onChange={(option) => {
            this.setState({ isSnippetVisible: false });
            void this.handleUpdateEmbed({ type: option.chartType });
          }}
          value={getVisualizationSelectedValue(embed.type || embed.data?.type)}
          valueComponent={VisualizationValue}
          className="test-graph-visualization-select"
          disabled={this.isSavedViewVisualizationsEmpty()}
        />
      </fieldset>
    );
  };

  renderDataDisplaySelectForV3Embeds = (isMultiDataset?: boolean) => {
    const {
      embed: { type },
      messages
    } = this.props;

    // Determine which options to use based on report type
    const options = isMultiDataset ? vizOptionsMds : vizOptionsChartsDetails;

    return (
      <fieldset className="ui-form">
        <Label>{messages.dataDisplay}</Label>
        <Select
          clearable={false}
          name="visualization"
          placeholder={messages.selectDataDisplay}
          className="test-graph-visualization-select"
          labelKey="name"
          valueKey="value"
          optionComponent={VisualizationOption}
          options={options}
          onChange={(option) => {
            this.setState({ isSnippetVisible: false });
            void this.handleUpdateEmbed({ type: option.value });
          }}
          value={type}
          valueComponent={VisualizationValue}
        />
      </fieldset>
    );
  };

  renderUpdateSelect = () => {
    const { updateSetting, v2EmbedFormDisabled } = this.state;
    const { messages } = this.props;
    const updateSettings = [
      {
        id: 'snapshot',
        name: messages.useSnapshot
      },
      {
        id: 'refresh-on-update',
        name: messages.refreshOnUpdate
      },
      {
        id: 'live',
        name: messages.liveView
      }
    ];

    return (
      <fieldset className="ui-form required">
        <Label>{messages.updateSetting}</Label>
        <Select
          data-test="graph-data-update-frequency"
          clearable={false}
          name="updateSetting"
          labelKey="name"
          valueKey="id"
          options={updateSettings}
          onChange={this.onUpdateSettingChange}
          value={updateSetting}
          resetValue={{ id: null, name: '' }}
          className="test-graph-update-setting-select"
          disabled={v2EmbedFormDisabled}
        />
      </fieldset>
    );
  };

  // Renders toggle for embed tile clickthrough
  renderClickSetting = () => {
    const { messages, embed } = this.props;
    const { disableClickThrough, v2EmbedFormDisabled } = this.state;

    const isV3Embed = embed?.version === 'v3';

    if (this.hasReportAndSavedView() && this.props.embed) {
      return (
        <ul>
          <li
            style={{
              marginBottom: '10px'
            }}
          >
            <Switch.Small
              id="embed-click-toggle"
              dataTest="embed-click-toggle"
              label={isV3Embed ? messages.reportLinkText : messages.enableClickThrough}
              labelOn={messages.yes}
              labelOff={messages.no}
              checked={!disableClickThrough}
              onChange={this.onClickThroughSettingChange}
              disabled={v2EmbedFormDisabled}
            />
          </li>
        </ul>
      );
    }
  };

  renderVisualization = (): JSX.Element => {
    const { embed, messages, reports } = this.props;
    const {
      embedVersion,
      report: { id: reportId }
    } = this.state;

    const selectedReport = reports.find((r) => r.id === reportId);

    if (this.hasReportAndSavedView()) {
      if (selectedReport && selectedReport.version === 'v2') {
        switch (embedVersion) {
          case 'v4':
            return <>{this.renderUpdateSelect()}</>;
          // By default the type is GroupedBarChart, see utils under embed-service
          case 'v3':
            return (
              <>
                {this.renderDataDisplaySelectForV3Embeds(selectedReport.isMultiDataset)}
                {this.renderUpdateSelect()}
                {selectedReport.isMultiDataset &&
                  embed.type === EmbedTypeV3.PieChart &&
                  this.renderColumnSelect(messages.reportColumnSetting, messages.reportColumnPlaceholder)}
                {selectedReport.isMultiDataset && embed.type === EmbedTypeV3.Gauge && this.renderGaugeFields()}
              </>
            );
          default:
            return (
              <>
                <p className={styles.noVisualizationMessage}>{messages.noVisualizationSupported}</p>
                {this.renderUpdateSelect()}
              </>
            );
        }
      }
      return (
        <>
          {this.renderDataDisplaySelect()}
          {this.renderUpdateSelect()}
          {this.renderTableVisualizationSettings()}
        </>
      );
    }

    return <p className={styles.message}>{messages.selectForVisualization}</p>;
  };

  renderGaugeFields = (): JSX.Element => {
    const { messages } = this.props;
    return (
      <>
        {this.renderColumnSelect(messages.measurementColumn, messages.measurementColumnPlaceholder)}
        {this.renderTargetColumnSelect()}
        {this.renderValueRowSelect()}
      </>
    );
  };

  renderValueRowSelect = (): JSX.Element => {
    const { embed, messages } = this.props;
    const embedDataOptions = (embed.data as IEmbedDataV3)?.options;

    const rowOptions = embedDataOptions?.rowChoices.map((option) => {
      return { id: option[0], name: option[1] };
    });

    return (
      <fieldset className="ui-form">
        <Label>{messages.valueRow}</Label>
        <Select
          clearable={false}
          name="valueRowSetting"
          placeholder={messages.valueRowPlaceholder}
          labelKey="name"
          valueKey="id"
          options={rowOptions ?? []}
          onChange={(rowOption) => {
            this.setState({ isSnippetVisible: false });
            void this.handleUpdateEmbed({ valueRowId: rowOption.id });
          }}
          value={embedDataOptions?.rowGroupId}
          className="graph-value-column-select"
        />
      </fieldset>
    );
  };

  renderTargetColumnSelect = (): JSX.Element => {
    const { embed, messages } = this.props;
    const embedDataOptions = (embed.data as IEmbedDataV3)?.options;

    const columnOptions = embedDataOptions?.columnChoices.map((option) => {
      return { id: option[0], name: option[1] };
    });

    return (
      <fieldset className="ui-form">
        <Label>{messages.targetColumn}</Label>
        <Select
          clearable={false}
          name="targetColumnSetting"
          placeholder={messages.targetColumnPlaceholder}
          labelKey="name"
          valueKey="id"
          options={columnOptions ?? []}
          onChange={(columnOption) => {
            this.setState({ isSnippetVisible: false });
            void this.handleUpdateEmbed({ targetColumnId: columnOption.id });
          }}
          value={embedDataOptions?.targetColumnId}
          className="graph-target-column-select"
        />
      </fieldset>
    );
  };

  renderColumnSelect = (label, placeholder): JSX.Element => {
    const { embed } = this.props;
    const embedDataOptions = (embed.data as IEmbedDataV3)?.options;

    const columnOptions = embedDataOptions?.columnChoices.map((option) => {
      return { id: option[0], name: option[1] };
    });

    return (
      <fieldset className="ui-form">
        <Label>{label}</Label>
        <Select
          clearable={false}
          name="columnSetting"
          placeholder={placeholder}
          labelKey="name"
          valueKey="id"
          options={columnOptions ?? []}
          onChange={(columnOption) => {
            this.setState({ isSnippetVisible: false });
            void this.handleUpdateEmbed({ columnId: columnOption.id });
          }}
          value={embedDataOptions?.columnId}
          className="graph-summary-column-select"
        />
      </fieldset>
    );
  };

  getTableColumnNames = (tableColumns: string[]): string[] => {
    const key = 'id';
    return tableColumns.map((column) => {
      if (column[key] && column[key] !== 'hide') return column[key];
    });
  };

  formatFiscalYears = (fiscalYears: string[]): { id: string; name: string }[] | null => {
    if (fiscalYears) {
      return fiscalYears.map((year) => {
        return { id: year, name: `FY${year}` };
      });
    }
    return null;
  };

  renderTableVisualizationSettings = () => {
    const { embed, messages, savedViewInfo, groupDataBy, setSelectedGroupings } = this.props;
    // If the visualization is a table && the data has more than just annual data
    if (savedViewInfo && embed.data?.type === 'transparency_table' && !savedViewInfo.hasAnnualDataOnly) {
      // Format fiscal years as column options
      const columnOptions = this.formatFiscalYears(savedViewInfo.fiscalYears);
      return (
        <div>
          <fieldset>
            <span className="comp-label">{messages.groupDataBy}</span>
            <div className={styles.radioButtonContainer}>
              <RadioGroup value={groupDataBy} onChange={this.onTableVisualizationSettingsGroupChange}>
                <RadioButton label="Category" value="category" tooltip="Select to Group by Category" />
                <RadioButton label="Month" value="month" tooltip="Select to Group by Month" />
              </RadioGroup>
            </div>
          </fieldset>
          {groupDataBy === 'month' && (
            <div>
              <TableColumnDropdowns
                messages={messages}
                handleCreateTile={(chartType, selectedGroupings) => {
                  this.setState({ isSnippetVisible: false });
                  this.handleUpdateEmbed({ type: chartType, selectedGroupings });
                }}
                columnOptions={columnOptions}
                selectedGroupings={this.props.selectedGroupings}
                setSelectedGroupings={setSelectedGroupings}
              />
            </div>
          )}
        </div>
      );
    }
  };

  renderEmbedSnippet = (): JSX.Element => {
    const { disableClickThrough, embedId, isUpdating, isSnippetVisible, embedVersion, v2EmbedFormDisabled } =
      this.state;
    const { messages } = this.props;
    let embedPrefix = embedVersion === 'v3' ? embedSdkApiv3Prefix : embedSdkApiv2Prefix;

    if (embedVersion === 'v4') {
      embedPrefix = embedSdkApiv4Prefix;
    }

    if (isUpdating && this.hasReportAndSavedView() && embedId) {
      return (
        <fieldset className="ui-form">
          <label>
            <span className="comp-label">{messages.embedSnippet}</span>
          </label>
          {isSnippetVisible ? (
            <textarea
              // tslint:disable-next-line max-line-length
              value={`<iframe src="${operateUrl}${embedPrefix}/${embedId}/iframe?disableClick=${disableClickThrough}" width=${EMBED_IFRAME_DEFAULT_WIDTH} height=${EMBED_IFRAME_DEFAULT_HEIGHT} title="OpenGov Story"></iframe>`}
              rows={3}
              readOnly={true}
            />
          ) : (
            <Button data-test="graph-embed-snippet" onClick={this.showEmbedSnippet} disabled={v2EmbedFormDisabled}>
              {messages.generate}
            </Button>
          )}
        </fieldset>
      );
    }
  };

  renderPreview = (): JSX.Element => {
    const { report, savedView } = this.state;
    const { embed, isCreatingEmbed, hasEmbedError, messages, isFetchingEmbed } = this.props;

    const previewKey = !isCreatingEmbed && embed && embed.id ? embed.id : `${report.id}-${savedView.id}`;
    let message;

    if (!this.hasReportAndSavedView()) {
      message = <p className={styles.message}>{messages.selectToPreview}</p>;
    } else if (hasEmbedError) {
      message = (
        <p className={styles.message} data-test="graph-error">
          {messages.errorCreatingTile}
        </p>
      );
    }

    const isClientRendered = Boolean(
      embed?.version === 'v4' ||
        embed?.version === 'v3' ||
        embed?.data?.isClientRendered ||
        embed?.data?.sdk?.isClientRendered
    );
    const showClickThroughToggle = Boolean(!isCreatingEmbed && !isFetchingEmbed && isClientRendered);
    const showEmbedSnippet = !isCreatingEmbed && !isFetchingEmbed;

    // Render embed tile preview
    return (
      <div className={cx(styles.previewColumn, 'ui-col-3of6')}>
        <fieldset className={cx('ui-form', styles.previewFieldset)}>
          <h4>{messages.tilePreview}</h4>
          {message}
          <div
            key={previewKey}
            className={cx(styles.preview, {
              [styles.hidden]: isCreatingEmbed || isFetchingEmbed
            })}
            id="tile-preview"
            data-test="graph-preview"
          />
          {(isCreatingEmbed || isFetchingEmbed) && (
            <LoadingSpinner
              className={styles.loadingPosition}
              message={messages.loadingPreview}
              data-test="graph-loading"
            />
          )}
        </fieldset>
        {showClickThroughToggle && this.renderClickSetting()}
        {showEmbedSnippet && this.renderEmbedSnippet()}
      </div>
    );
  };

  renderViewButton = (): JSX.Element => {
    const { embed, messages } = this.props;
    const { hasFailedReportEmbed, v2EmbedFormDisabled } = this.state;
    const publicUrl = embed.publicUrl || embed.data?.publicUrl || embed.data?.tile?.publicUrl;
    const privateUrl = embed.privateUrl || embed.data?.privateUrl || embed.data?.tile?.privateUrl;
    // Render view button if Report and Saved View are selected
    if (this.hasReportAndSavedView() && !hasFailedReportEmbed && (publicUrl || privateUrl) && !v2EmbedFormDisabled) {
      return (
        <a href={publicUrl || privateUrl} target="_blank" rel="noopener noreferrer">
          <Button data-test="graph-view">{messages.goToView}</Button>
        </a>
      );
    }

    // By default, render a disabled button
    return (
      <Button data-test="graph-view" disabled={true}>
        {messages.goToView}
      </Button>
    );
  };

  renderSubmitButton = (): JSX.Element => {
    const { isUpdating, report, savedView, shouldDisableButton, v2EmbedFormDisabled } = this.state;
    const { isCreatingEmbed, hasEmbedError, messages, groupDataBy, selectedGroupings } = this.props;
    const submitText = isUpdating ? messages.update : messages.add;

    // The default value for groupDataBy is category
    const isGroupedByCategory: boolean = groupDataBy === 'category';
    // If grouping by month, selectedGroupings should be populated to enable adding a tile
    const isGroupedByMonthWithSelections: boolean =
      groupDataBy === 'month' && selectedGroupings && selectedGroupings.length > 0;

    // Render add button if Report, Saved View, and valid data grouping settings are selected
    if (
      !v2EmbedFormDisabled &&
      !isCreatingEmbed &&
      !hasEmbedError &&
      !shouldDisableButton &&
      report.id &&
      savedView.id &&
      (isGroupedByCategory || isGroupedByMonthWithSelections)
    ) {
      return (
        <Button onClick={this.handleClick} variant="primary" data-test="graph-save">
          {submitText}
        </Button>
      );
    }

    // By default, render a disabled button
    return (
      <Button disabled={true} variant="primary" data-test="graph-save">
        {submitText}
      </Button>
    );
  };

  render() {
    const { isUpdating, report, savedView, shouldDisableButton, hasFailedReportEmbed, v2EmbedFormDisabled } =
      this.state;
    const {
      isRequestingReports,
      isRequestingSavedViews,
      isReportEmbedListSelected,
      reports,
      savedViews,
      messages,
      isCreatingEmbed,
      isFetchingEmbed
    } = this.props;
    const title = isUpdating || isReportEmbedListSelected ? messages.updateTile : messages.addTile;

    return (
      <>
        {this.state.showDeleteModal &&
          createPortal(
            <RemoveEmbedModal
              entityId={this.props.entityId}
              onClose={() => {
                this.setState({ showDeleteModal: false });
              }}
              deleteReportEmbed={this.handleRemove}
              messages={this.props.messages}
              isLoading={this.state.isDeleteEmbedinProgress}
            />,
            document.getElementById('app')
          )}
        <Modal title={title} onClose={this.handleClose}>
          <div className={cx(styles.container)}>
            {v2EmbedFormDisabled ? (
              <div className="ui-alert-box notice" data-test="edit-v2-embed-alert">
                <span className="icon-exclamation-1"></span>
                {messages.v2EmbedFormDisabled}
              </div>
            ) : null}
            <div className="ui-row">
              <div className="ui-col-3of6">
                <h4>{messages.configurations}</h4>
                <fieldset className="ui-form">
                  <Form.Item label={messages.report}>
                    <Select
                      name="report"
                      labelKey="name"
                      valueKey="id"
                      options={reports}
                      isLoading={isRequestingReports}
                      placeholder={messages.selectReport}
                      loadingPlaceholder={messages.searchingForReports}
                      noResultsText={messages.noReports}
                      onChange={this.onReportChange}
                      value={report.id}
                      resetValue={{ id: null, name: '' }}
                      className="test-graph-report-select"
                      optionComponent={ReportOption}
                      disabled={isCreatingEmbed || isFetchingEmbed || v2EmbedFormDisabled}
                    />
                    {hasFailedReportEmbed && (
                      <div className={styles.errorFailedEmbed}>
                        <Icon icon="status-warning" className={styles.errorIcon} />
                        {messages.reportFailedToLoad}
                      </div>
                    )}
                  </Form.Item>
                </fieldset>
                <fieldset className="ui-form">
                  <Form.Item label={messages.savedView}>
                    <div className="ui-row">
                      <div className="ui-col-4of6">
                        <Select
                          name="savedView"
                          labelKey="name"
                          valueKey="id"
                          options={savedViews}
                          isLoading={isRequestingSavedViews}
                          placeholder={messages.selectSavedView}
                          loadingPlaceholder={messages.searchingForSavedViews}
                          noResultsText={messages.noSavedViews}
                          onChange={this.onSavedViewChange}
                          value={savedView.id}
                          resetValue={{ id: null, name: '' }}
                          className="test-graph-saved-view-select"
                          disabled={isCreatingEmbed || isFetchingEmbed || v2EmbedFormDisabled}
                        />
                      </div>
                      <div className={cx(styles.buttonColumn, 'ui-col-2of6')}>{this.renderViewButton()}</div>
                    </div>
                    <div>{}</div>
                  </Form.Item>
                </fieldset>
                {this.renderTitleInput()}
                {this.renderDescriptionInput()}
                <h4>{messages.visualization}</h4>
                {this.renderVisualization()}
              </div>
              {this.renderPreview()}
            </div>
          </div>
          <div className={modalStyles.buttons}>
            <Button onClick={this.handleClose} data-test="graph-cancel" disabled={shouldDisableButton}>
              {messages.cancel}
            </Button>
            {isUpdating && (
              <Button
                variant="destructive"
                onClick={() => {
                  this.props.isReportEmbedListSelected ? this.setState({ showDeleteModal: true }) : this.handleRemove();
                }}
                loading={this.state.isDeleteEmbedinProgress}
                disabled={!this.hasReportAndSavedView() && !hasFailedReportEmbed}
              >
                {messages.remove}
              </Button>
            )}
            {this.renderSubmitButton()}
          </div>
        </Modal>
      </>
    );
  }
}
